'use client'

import { useShopTheLook } from 'cross-selling/contexts/ShopTheLookProvider/ShopTheLookProvider'
import { Content } from 'fukku/Dialog/Content/Content'
import type { DialogAPI } from 'fukku/Dialog/Dialog'
import { Header } from 'fukku/Dialog/Header/Header'
import { SideSheet } from 'fukku/Dialog/SideSheet/SideSheet'
import { IconDone } from 'icons/components/IconDone'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import dynamic from 'next/dynamic'
import {
	Snackbar,
	type SnackbarHandler,
} from 'notifications/components/Snackbar/Snackbar'
import { SnackbarPosition } from 'notifications/constants/constants'
import { useCallback, useEffect, useRef } from 'react'

const DynamicShopTheLookContent = dynamic(() =>
	import('./ShopTheLookContent/ShopTheLookContent').then(
		(mod) => mod.ShopTheLookContent
	)
)

const NOTIFICATION_DELAY = 4000

type ShopTheLookCrossSellingProps = {
	readonly modalTitle: string
	readonly dataTestId?: string
}

export function ShopTheLookCrossSelling({
	modalTitle,
	dataTestId,
}: ShopTheLookCrossSellingProps): React.ReactNode {
	const { t } = useLabels()
	const sideSheetRef = useRef<DialogAPI>(null)
	const snackbarRef = useRef<SnackbarHandler | null>(null)
	const {
		country: { hasOnlineSale },
	} = useMasterData()
	const { isOpen, setIsOpen, products } = useShopTheLook()

	const onAddToCart = () => {
		snackbarRef.current?.open()
	}

	const onClose = useCallback((): void => {
		setIsOpen(false)
		snackbarRef.current?.close()
	}, [])

	useEffect(() => {
		if (isOpen) {
			sideSheetRef.current?.show()
		}
	}, [isOpen])

	if (products.length === 0) {
		return null
	}

	return (
		<SideSheet
			ref={sideSheetRef}
			title={modalTitle}
			onClose={onClose}
			data-testid={dataTestId}
		>
			<Header title={modalTitle} />
			<Content>
				<DynamicShopTheLookContent
					hasOnlineSale={hasOnlineSale}
					products={products}
					onAddToCart={onAddToCart}
				/>
			</Content>
			<Snackbar
				delay={NOTIFICATION_DELAY}
				ref={snackbarRef}
				id='addItemNotification'
				title={t('snackbar.addToBag.confirmation.text')}
				icon={<IconDone color='content-inverse' />}
				position={SnackbarPosition.TOP}
			/>
		</SideSheet>
	)
}
